import "../../css/FullEvent/FullEvent.css";
import "../../css/FullEvent/ShareTickets.css";
import "../../css/FullEvent/EventDashboard.css";
import "react-loading-skeleton/dist/skeleton.css";
import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchReadyForPayment,
  getAccountState,
} from "../../redux/slices/accountSlice";
import { Colors } from "../../utils/colors";
import {
  AccountData,
  DefaultLinkTrackerName,
  Event,
  OrganizerType,
  RequestStatus,
  TicketV2,
} from "@markit/common.types";
import {
  checkEventHasRevenue,
  fetchAllTicketUserData,
  getTotalPromoterRevenue,
  getTotalRevShareTicketsSoldByTicket,
} from "../../utils/eventUtils/eventUtils";
import { TabPanel } from "../../components/FullEventSubComponents/TabPanel";
import LoadingScreen from "../LoadingScreen";
import Footer from "../../components/Footer";
import {
  isEventExternalLink,
  isExternalEventbrite,
  isExternalIntegration,
  isExternalLinkNoData,
  isExternalShowclix,
  isLinkedEventType,
  isRevShareOrganizerTicket,
} from "@markit/common.utils";
import ShareEventModal from "../../components/EventDashboard/DashboardModals/ShareEventModal";
import { useLiveUpdatingEventAnalytics } from "../../hooks/useLiveUpdatingEventAnalytics";
import { AnalyticsPanel } from "../../components/EventDashboard/AnalyticsPanel";
import { getUserEventLinkTrackersRef } from "../../utils/FirebaseUtils";
import { onSnapshot, query, where } from "../../firebase";
import { TrackingLinkModal } from "../../components/EventDashboard/DashboardModals/TrackingLinkModal";
import { PromoCodePanel } from "../../components/EventDashboard/PromoCodePanel";
import { FormsPanel } from "../../components/EventDashboard/FormsPanel";
import EventRoleModal from "../../components/EventDashboard/DashboardModals/EventRoleModal";
import HelpModal from "../../components/Containers/HelpModal";
import { EventDashboardHeader } from "../../components/EventDashboard/EventDashboardHeader";
import { EventDashboardTabs } from "../../components/EventDashboard/EventDashboardTabs";
import { TextsPanel } from "../../components/EventDashboard/TextsPanel";
import { SettingsPanel } from "../../components/EventDashboard/SettingsPanel";
import { GuestListPanel } from "../../components/EventDashboard/GuestListPanel";
import { useLoadUserTicketList } from "../../hooks/useLoadUserTicketList";
import { MixpanelContext } from "../../context/AnalyticsService";
import EnablePaidTicketsModal from "../../components/DisplayModal/EnablePaidTicketsModal";
import { EventbriteEventPanel } from "../../components/EventDashboard/EventbriteEventPanel";
import { ShowclixEventPanel } from "../../components/EventDashboard/ShowclixEventPanel";
import CreatorModeWrapper from "../../components/CreatorDashboard/CreatorModeWrapper";
import { useLoadTrackingLinksList } from "../../hooks/useLoadTrackingLinksList";
import { API } from "../../API";
import { useLiveUpdatingCampaign } from "../../hooks/useLiveHooks/useLiveUpdatingCampaign";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import FullProfilePreviewModal from "../../components/FollowerProfile/FullProfilePreviewModal";

type EventDashboardProps = {
  event: Event;
  userTicket: TicketV2 | undefined;
};

const EventDashboard = memo(function EventDashboardFn(
  props: EventDashboardProps
) {
  const { event, userTicket } = props;
  const { account } = useSelector(getAccountState);
  const { accountData, readyForPayment, followingAccountData } = account;
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);

  const {
    liveTotalGuests,
    liveTicketsScanned,
    liveTotalRequested,
    liveTotalRefunded,
    liveTotalOrganizers,
    liveTotalLinkViews,
  } = useLiveUpdatingEventAnalytics({
    eventId: event.id,
    userId: event?.createdBy,
  });

  const {
    liveCampaign: campaign,
    liveCampaignDetail,
    loaded: campaignLoaded,
  } = useLiveUpdatingCampaign({
    initialCampaignId: "",
    eventId: event.id,
    userId: accountData.uid,
  });

  const [loading, setLoading] = useState(true);
  const [dashboardValue, setDashboardValue] = useState(0);
  const [allTicketUserData, setAllTicketUserData] = useState<AccountData[]>([]);
  const [hasPaidRevenue, setHasPaidRevenue] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [profilePreviewSelected, setProfilePreviewSelected] =
    useState<AccountData>();
  const [isEventRoleModalOpen, setIsEventRoleModalOpen] = useState(false);
  const [roleOrganizerTicketItem, setRoleOrganizerTicketItem] =
    useState<TicketV2>();
  const [roleOrganizerData, setRoleOrganizerData] = useState<AccountData>();
  const [promoterTicketsSold, setPromoterTicketsSold] = useState(0);
  const [promoterRevenue, setPromoterRevenue] = useState(0);
  const [numTotalLinkTrackers, setNumTotalLinkTrackers] = useState(0);
  const [isTrackingLinksView, setIsTrackingLinksView] = useState(false);
  const [trackingLinkModalVisible, setTrackingLinkModalVisible] =
    useState(false);
  const [linkTrackerId, setLinkTrackerId] = useState<string | undefined>(
    undefined
  );

  const [trackingLinkModalText, setTrackingLinkModalText] = useState("");
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [stripeVisible, setStripeVisible] = useState<boolean>(false);

  const isExternalLink = useMemo(
    () => (event ? isEventExternalLink(event.eventType) : false),
    [event]
  );

  const styles = {
    headerView: {
      padding: "30px 30px 0px 30px",
      backgroundColor: Colors.WHITE,
    },

    contentView: {
      height: "100%",
      padding: 30,
      paddingBottom: 60,
      backgroundColor: Colors.WHITE1,
    },
  };

  const dashboardVisibility = useMemo(
    () =>
      Math.max(
        accountData.isAdmin ? 2 : 0,
        userTicket ? userTicket.role.dashboardVisibility : 0
      ),
    [accountData.isAdmin, userTicket]
  );

  const canMassText = useMemo(
    () =>
      accountData.isAdmin || (userTicket ? userTicket.role.massTexting : false),
    [accountData.isAdmin, userTicket]
  );

  const checkInGuests = useMemo(
    () =>
      accountData.isAdmin ||
      (userTicket ? userTicket.role.checkInGuests : false),
    [accountData.isAdmin, userTicket]
  );

  const isRevShare = useMemo(
    () => !!userTicket && isRevShareOrganizerTicket(userTicket),
    [userTicket]
  );

  const isPaymentCollectorRole = useMemo(
    () =>
      userTicket?.role.alternatePaymentCollector ||
      userTicket?.role.type === OrganizerType.ALTERNATEPAYMENTCOLLECTOR,
    [userTicket?.role.alternatePaymentCollector, userTicket?.role.type]
  );

  const isHost = useMemo(
    () => (event ? accountData.uid === event.createdBy : false),
    [accountData.uid, event]
  );

  // Checks if the campaign is loading still, only if the event is not an external link or eventbrite
  const isCampaignLoading = useMemo(
    () =>
      !campaignLoaded &&
      event &&
      !isExternalLinkNoData(event.eventType) &&
      !isExternalIntegration(event.eventType) &&
      isHost,
    [campaignLoaded, event, isHost]
  );

  const openShareModal = useCallback(
    async (source: string) => {
      mixpanel.track("Pressed Event Dashboard Share", {
        event_id: event?.id,
        type: event?.eventType,
        in_app: false,
        source: source,
      });
      if ((isRevShare || isPaymentCollectorRole) && !readyForPayment) {
        setStripeVisible(true);
      } else {
        setIsShareModalOpen(true);
      }
    },
    [
      mixpanel,
      event?.id,
      event?.eventType,
      isRevShare,
      isPaymentCollectorRole,
      readyForPayment,
    ]
  );

  const closeShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);

  /* Start Tab viewing code */
  const isEventbrite = useMemo(
    () => (event ? isExternalEventbrite(event.eventType) : false),
    [event]
  );

  const isShowclix = useMemo(
    () => (event ? isExternalShowclix(event.eventType) : false),
    [event]
  );

  const viewTextsTab = useMemo(
    () =>
      ((userTicket && canMassText) || accountData.isAdmin) && event
        ? !isExternalLinkNoData(event.eventType)
        : false,
    [accountData.isAdmin, canMassText, event, userTicket]
  );

  const viewAnalyticsTab = useMemo(
    () => checkInGuests || isPaymentCollectorRole,
    [checkInGuests, isPaymentCollectorRole]
  );

  const viewFormsTab = useMemo(
    () =>
      checkInGuests && (event ? !isExternalLinkNoData(event.eventType) : false),
    [checkInGuests, event]
  );

  const viewPromosTab = useMemo(
    () =>
      (userTicket?.role.distributeTicketsAndCodes || accountData.isAdmin) &&
      !isExternalLink,
    [
      accountData.isAdmin,
      isExternalLink,
      userTicket?.role.distributeTicketsAndCodes,
    ]
  );

  const viewSettingsTab = useMemo(
    () => dashboardVisibility >= 2 && !isLinkedEventType(event.eventType),
    [dashboardVisibility, event.eventType]
  );

  // Determines the tab index based on the visibility of other tabs
  const calculateTabIndex = useCallback(
    (conditions: boolean[]) => conditions.filter(Boolean).length + 1,
    []
  );
  /* End Tab viewing code */

  /* Guest List code */
  const {
    isFinished: isAttendeeFinished,
    isLoading: isAttendeeLoading,
    fetchedUserData: fullAttendeeAccountData,
    fetchedTicketData: fullAttendeeTicketData,
    loadTicketUsers: loadAttendeeUsers,
    loadMoreTicketUsers: loadMoreAttendeeUsers,
    loadSearchResultsThrottled: loadAttendeeSearchResultsThrottled,
    loadTicketsForUid: loadAttendeeTicketsForUid,
  } = useLoadUserTicketList({
    event: event,
    windowSize: 15,
    ticketType: RequestStatus.ACCEPTED,
    preloadedUserData: allTicketUserData,
  });

  const {
    isFinished: isPendingRequestsFinished,
    isLoading: isPendingRequestsLoading,
    fetchedUserData: fullPendingRequestsAccountData,
    fetchedTicketData: fullPendingRequestsTicketData,
    loadTicketUsers: loadPendingRequestsUsers,
    loadMoreTicketUsers: loadMorePendingRequestsUsers,
    loadSearchResultsThrottled: loadPendingRequestsSearchResultsThrottled,
    loadTicketsForUid: loadPendingRequestsTicketsForUid,
  } = useLoadUserTicketList({
    event: event,
    windowSize: 15,
    ticketType: RequestStatus.PENDING,
    preloadedUserData: allTicketUserData,
  });

  const {
    isFinished: isOrganizerFinished,
    isLoading: isOrganizerLoading,
    fetchedUserData: fullOrganizerAccountData,
    fetchedTicketData: fullOrganizerTicketData,
    loadTicketUsers: loadOrganizerUsers,
    loadMoreTicketUsers: loadMoreOrganizerUsers,
    loadSearchResultsThrottled: loadOrganizerSearchResultsThrottled,
    loadTicketsForUid: loadOrganizerTicketsForUid,
  } = useLoadUserTicketList({
    event: event,
    windowSize: 15,
    ticketType: "organizers",
    preloadedUserData: allTicketUserData,
  });

  const {
    isFinished: isRefundedFinished,
    isLoading: isRefundedLoading,
    fetchedUserData: fullRefundedAccountData,
    fetchedTicketData: fullRefundedTicketData,
    loadTicketUsers: loadRefundedUsers,
    loadMoreTicketUsers: loadMoreRefundedUsers,
    loadSearchResultsThrottled: loadRefundedSearchResultsThrottled,
    loadTicketsForUid: loadRefundedTicketsForUid,
  } = useLoadUserTicketList({
    event: event,
    windowSize: 15,
    ticketType: RequestStatus.REFUNDED,
    preloadedUserData: allTicketUserData,
  });
  /* End Guest List code */

  /* Start Analytics Tab code */
  const {
    isLoading: isLoadingTrackingLinks,
    isFinished: isFinishedTrackingLinks,
    fetchedLinkTrackers,
    loadSearchResultsThrottled,
    fetchMoreTrackingLinks,
    fetchTrackingLinks,
  } = useLoadTrackingLinksList({
    userId: event ? event.createdBy : accountData.uid,
    eventId: event.id,
    windowSize: 20,
  });
  /* End Analytics Tab code */

  // TODO: Create a custom hook wrapper that can load all the required data, so we don't need to pass it as props throughout
  useAsyncOnMount(async () => {
    const ticketsUserData = await fetchAllTicketUserData(
      event.id,
      followingAccountData
    );
    setAllTicketUserData(ticketsUserData);

    const [hasPaidRevenue, { numUserEventLinkTrackers }] = await Promise.all([
      checkEventHasRevenue(event.id),
      API.tracking.getNumUserEventLinkTrackers({
        eventUserId: event.createdBy,
        eventId: event.id,
      }),
      fetchTrackingLinks(),
      loadAttendeeUsers(),
      loadPendingRequestsUsers(),
      loadOrganizerUsers(),
      loadRefundedUsers(),
    ]);
    setHasPaidRevenue(hasPaidRevenue);
    setNumTotalLinkTrackers(numUserEventLinkTrackers);

    if (userTicket) {
      // for any cohost/promoter with rev share enabled, or the payment collector,
      // check their own Stripe readiness
      if (isRevShare || isPaymentCollectorRole) {
        dispatch(fetchReadyForPayment(accountData.uid));
      }

      // for any cohost or promoter with rev share enabled
      if (isRevShare && userTicket.role.dashboardVisibility < 2) {
        const [ticketsSold, revenues] = await Promise.all([
          getTotalRevShareTicketsSoldByTicket(event.id, userTicket.id),
          getTotalPromoterRevenue(event.id, accountData.uid),
        ]);
        setPromoterTicketsSold(ticketsSold);
        const { promoterRevenue } = revenues;
        setPromoterRevenue(promoterRevenue);
      }
    }

    const userEventLinkTrackersRef = getUserEventLinkTrackersRef(
      event.createdBy,
      event.id
    );
    const query_ = query(
      userEventLinkTrackersRef,
      where("alias", "!=", DefaultLinkTrackerName.DEFAULT_EVENT_LINK_TRACKER)
    );
    const unsubscribe = onSnapshot(query_, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === "modified") {
          const { numUserEventLinkTrackers } =
            await API.tracking.getNumUserEventLinkTrackers({
              eventUserId: event.createdBy,
              eventId: event.id,
            });
          setNumTotalLinkTrackers(numUserEventLinkTrackers);
        }
      });
    });

    setLoading(false);
    return () => unsubscribe();
  });

  if (isCampaignLoading || loading) {
    return <LoadingScreen isLoadingPage={isCampaignLoading || loading} />;
  }

  return (
    <CreatorModeWrapper altBackgroundColor={Colors.WHITE1}>
      <div style={styles.headerView}>
        <EventDashboardHeader
          event={event}
          userTicket={userTicket}
          openShareModal={openShareModal}
        />
        <EventDashboardTabs
          dashboardValue={dashboardValue}
          setDashboardValue={setDashboardValue}
          viewTextsTab={viewTextsTab}
          viewAnalyticsTab={viewAnalyticsTab}
          viewFormsTab={viewFormsTab}
          viewPromosTab={viewPromosTab}
          viewSettingsTab={viewSettingsTab}
          eventType={event.eventType}
        />
      </div>
      <div style={styles.contentView}>
        {isEventbrite ? (
          <TabPanel value={dashboardValue} index={0}>
            <EventbriteEventPanel eventbriteEventId={event.externalEventId} />
          </TabPanel>
        ) : isShowclix ? (
          <>
            <TabPanel value={dashboardValue} index={0}>
              <ShowclixEventPanel
                event={event}
                campaign={campaign}
                campaignDetail={liveCampaignDetail}
              />
            </TabPanel>
            <TabPanel value={dashboardValue} index={1}>
              <TextsPanel
                event={event}
                campaign={campaign}
                campaignDetail={liveCampaignDetail}
              />
            </TabPanel>
          </>
        ) : (
          <>
            <TabPanel value={dashboardValue} index={0}>
              <GuestListPanel
                event={event}
                userTicket={userTicket}
                totalGuests={liveTotalGuests}
                totalRequested={liveTotalRequested}
                totalOrganizers={liveTotalOrganizers}
                totalRefunded={liveTotalRefunded}
                totalLinkViews={liveTotalLinkViews}
                promoterTicketsSold={promoterTicketsSold}
                promoterRevenue={promoterRevenue}
                checkInGuests={checkInGuests}
                canMassText={canMassText}
                dashboardVisiblity={dashboardVisibility}
                isHost={isHost}
                loading={loading}
                campaign={campaign}
                campaignDetail={liveCampaignDetail}
                setIsEventRoleModalOpen={setIsEventRoleModalOpen}
                setRoleOrganizerData={setRoleOrganizerData}
                setRoleOrganizerTicketItem={setRoleOrganizerTicketItem}
                setProfilePreviewSelected={setProfilePreviewSelected}
                isAttendeeLoading={isAttendeeLoading}
                fullAttendeeTicketData={fullAttendeeTicketData}
                fullAttendeeAccountData={fullAttendeeAccountData}
                loadMoreAttendeeUsers={loadMoreAttendeeUsers}
                loadAttendeeSearchResultsThrottled={
                  loadAttendeeSearchResultsThrottled
                }
                loadAttendeeTicketsForUid={loadAttendeeTicketsForUid}
                isPendingRequestsLoading={isPendingRequestsLoading}
                fullPendingRequestsTicketData={fullPendingRequestsTicketData}
                fullPendingRequestsAccountData={fullPendingRequestsAccountData}
                loadMorePendingRequestsUsers={loadMorePendingRequestsUsers}
                loadPendingRequestsSearchResultsThrottled={
                  loadPendingRequestsSearchResultsThrottled
                }
                loadPendingRequestsTicketsForUid={
                  loadPendingRequestsTicketsForUid
                }
                isOrganizerLoading={isOrganizerLoading}
                fullOrganizerTicketData={fullOrganizerTicketData}
                fullOrganizerAccountData={fullOrganizerAccountData}
                loadMoreOrganizerUsers={loadMoreOrganizerUsers}
                loadOrganizerSearchResultsThrottled={
                  loadOrganizerSearchResultsThrottled
                }
                loadOrganizerTicketsForUid={loadOrganizerTicketsForUid}
                isRefundedLoading={isRefundedLoading}
                fullRefundedTicketData={fullRefundedTicketData}
                fullRefundedAccountData={fullRefundedAccountData}
                loadMoreRefundedUsers={loadMoreRefundedUsers}
                loadRefundedSearchResultsThrottled={
                  loadRefundedSearchResultsThrottled
                }
                loadRefundedTicketsForUid={loadRefundedTicketsForUid}
                isRefundedFinished={isRefundedFinished}
                isAttendeeFinished={isAttendeeFinished}
                isPendingRequestsFinished={isPendingRequestsFinished}
                isOrganizerFinished={isOrganizerFinished}
              />
            </TabPanel>
            {viewTextsTab ? (
              <TabPanel value={dashboardValue} index={1}>
                <TextsPanel
                  event={event}
                  campaign={campaign}
                  campaignDetail={liveCampaignDetail}
                />
              </TabPanel>
            ) : null}
            {viewAnalyticsTab ? (
              <TabPanel
                value={dashboardValue}
                index={calculateTabIndex([viewTextsTab])}
              >
                <AnalyticsPanel
                  event={event}
                  totalGuests={liveTotalGuests}
                  totalLinkViews={liveTotalLinkViews}
                  canMassText={canMassText}
                  fetchedLinkTrackers={fetchedLinkTrackers}
                  isFinishedTrackingLinks={isFinishedTrackingLinks}
                  isLoadingTrackingLinks={isLoadingTrackingLinks}
                  loadSearchResultsThrottled={loadSearchResultsThrottled}
                  fetchMoreTrackingLinks={fetchMoreTrackingLinks}
                  setTrackingLinkModalVisible={setTrackingLinkModalVisible}
                  numTotalLinkTrackers={numTotalLinkTrackers}
                  linkTrackerId={linkTrackerId}
                  setLinkTrackerId={setLinkTrackerId}
                  setTrackingLinkModalText={setTrackingLinkModalText}
                  ticketAnalyticsData={{
                    userTicket,
                    totalScanned: liveTicketsScanned,
                    dashboardVisibility,
                    isPaymentCollectorRole,
                  }}
                />
              </TabPanel>
            ) : null}
            {viewFormsTab ? (
              <TabPanel
                value={dashboardValue}
                index={calculateTabIndex([viewTextsTab, viewAnalyticsTab])}
              >
                <FormsPanel
                  event={event}
                  dashboardVisibility={dashboardVisibility}
                />
              </TabPanel>
            ) : null}
            {viewPromosTab ? (
              <TabPanel
                value={dashboardValue}
                index={calculateTabIndex([
                  viewTextsTab,
                  viewAnalyticsTab,
                  viewFormsTab,
                ])}
              >
                <PromoCodePanel event={event} />
              </TabPanel>
            ) : null}
            {viewSettingsTab ? (
              <TabPanel
                value={dashboardValue}
                index={calculateTabIndex([
                  viewTextsTab,
                  viewAnalyticsTab,
                  viewFormsTab,
                  viewPromosTab,
                ])}
              >
                <SettingsPanel
                  event={event}
                  userTicket={userTicket}
                  totalGuests={liveTotalGuests ?? 0}
                  hasPaidRevenue={hasPaidRevenue}
                  dashboardVisibility={dashboardVisibility}
                  isHost={isHost}
                />
              </TabPanel>
            ) : null}
          </>
        )}
      </div>
      {isShareModalOpen ? (
        <ShareEventModal
          event={event}
          userTicket={userTicket}
          revShare={isRevShare}
          hideTrackingLinks={
            (userTicket ? userTicket.role.dashboardVisibility < 2 : true) ||
            isEventbrite
          }
          closeShareModal={closeShareModal}
          isTrackingLinksView={isTrackingLinksView}
          setIsTrackingLinksView={setIsTrackingLinksView}
          trackingLinkData={fetchedLinkTrackers}
          fetchMoreTrackingLinks={fetchMoreTrackingLinks}
          canLoadMoreTrackingLinks={
            !isFinishedTrackingLinks && !isLoadingTrackingLinks
          }
          setTrackingLinkModalVisible={setTrackingLinkModalVisible}
        />
      ) : null}
      {trackingLinkModalVisible ? (
        <TrackingLinkModal
          event={event}
          linkTrackerId={linkTrackerId}
          setLinkTrackerId={setLinkTrackerId}
          trackingLinkModalText={trackingLinkModalText}
          setTrackingLinkModalText={setTrackingLinkModalText}
          setTrackingLinkModalVisible={setTrackingLinkModalVisible}
          openShareModal={openShareModal}
          setIsTrackingLinksView={setIsTrackingLinksView}
        />
      ) : null}
      {profilePreviewSelected ? (
        <FullProfilePreviewModal
          profileSelected={profilePreviewSelected}
          setProfileSelected={setProfilePreviewSelected}
          event={event}
          userTicket={userTicket}
        />
      ) : null}
      {!accountData.inCreatorMode ? <Footer showRCA /> : null}
      {isEventRoleModalOpen ? (
        <EventRoleModal
          event={event}
          organizerTicketItem={roleOrganizerTicketItem}
          organizerData={roleOrganizerData}
          setOrganizerTicketItem={setRoleOrganizerTicketItem}
          setOrganizerData={setRoleOrganizerData}
          isEventRoleModalOpen={isEventRoleModalOpen}
          setIsEventRoleModalOpen={setIsEventRoleModalOpen}
          setProfilePreviewSelected={setProfilePreviewSelected}
        />
      ) : null}
      <HelpModal showModal={showHelpModal} setShowModal={setShowHelpModal} />
      {stripeVisible ? (
        <EnablePaidTicketsModal
          isVisible={stripeVisible}
          setIsVisible={setStripeVisible}
          isPromoter={isRevShare}
        />
      ) : null}
    </CreatorModeWrapper>
  );
});

export default EventDashboard;
